import { createTheme } from '@mui/material/styles';
import { darkPalette, lightPalette, cloud2DarkPalette, cloud2LightPalette, mrRobotPalette } from './palette';

export type ThemeName = 'dark' | 'light' | 'cloud2-dark' | 'cloud2-light' | 'mr-robot';

const paletteMap = {
  dark: darkPalette,
  light: lightPalette,
  'cloud2-dark': cloud2DarkPalette,
  'cloud2-light': cloud2LightPalette,
  'mr-robot': mrRobotPalette,
} as const;

export function buildTheme(themeName: ThemeName) {
  const palette = paletteMap[themeName];
  const mode = palette.mode!;

  const isMrRobot = themeName === 'mr-robot';
  const scrollbarTrack = isMrRobot ? '#0a0a0a' : mode === 'dark' ? '#374151' : '#e5e7eb';
  const scrollbarThumb = isMrRobot ? '#00cc33' : mode === 'dark' ? '#6b7280' : '#9ca3af';

  return createTheme({
    palette,
    typography: {
      fontFamily: isMrRobot
        ? '"Share Tech Mono", "Rajdhani", "Roboto Mono", monospace'
        : '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: 'thin',
            scrollbarColor: `${scrollbarThumb} ${scrollbarTrack}`,
            '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
              backgroundColor: scrollbarTrack,
              width: '8px',
            },
            '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
              borderRadius: 8,
              backgroundColor: scrollbarThumb,
              minHeight: 24,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
          }),
          notchedOutline: ({ theme }) => ({
            borderColor: theme.palette.divider,
          }),
        },
      },
      ...(mode === 'light' && {
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
            },
          },
        },
        MuiDialog: {
          styleOverrides: {
            paper: {
              border: `1px solid ${palette.divider || '#e2e8f0'}`,
            },
          },
        },
      }),
    },
  });
}
